import { useId } from 'react'
import type { Room } from './mascot-game-types'
import sceneStyles from './MascotScene.module.css'
import styles from './FarmDrawing.module.css'

/** Shared hand-drawn farm landmarks, also used by the profile and frisbee scenery. */
export function FarmBarn() {
  return (
    <g>
      <path d="M0 65 27 20 85 0 143 20 170 65v108H0Z" fill="#b95745" />
      <path
        d="m-8 69 32-55L85-7l61 21 32 55"
        fill="none"
        stroke="#694f40"
        strokeWidth="12"
        strokeLinejoin="round"
      />
      <path
        d="M12 77v86m18-95v95m18-95v95m74-95v95m18-95v95m18-86v86"
        stroke="#e58c68"
        strokeWidth="3"
        opacity=".6"
      />
      <rect
        x="47"
        y="95"
        width="76"
        height="78"
        rx="2"
        fill="#743e35"
        stroke="#fff0cb"
        strokeWidth="5"
      />
      <path d="m49 98 72 72m0-72-72 72M85 98v73" stroke="#fff0cb" strokeWidth="4" />
      <rect
        x="66"
        y="37"
        width="38"
        height="32"
        rx="3"
        fill="#473e36"
        stroke="#ffe8bb"
        strokeWidth="5"
      />
      <path d="M85 39v27m-16-13h31" stroke="#ffe8bb" strokeWidth="3" />
      <path d="M-5 175h180" stroke="#806342" strokeWidth="7" strokeLinecap="round" />
    </g>
  )
}

export function FarmWindmill({ rotation }: { rotation?: number }) {
  return (
    <g>
      <path
        d="m-30 170 21-145h18l21 145M-23 127h46M-15 74h30m-43 85 47-72m-45 0 48 72"
        fill="none"
        stroke="#8c7960"
        strokeWidth="5"
      />
      <g
        className={rotation === undefined ? styles.rotor : undefined}
        transform={rotation === undefined ? undefined : `rotate(${rotation})`}
      >
        {[0, 60, 120, 180, 240, 300].map((angle) => (
          <g key={angle} transform={`rotate(${angle})`}>
            <path d="M0 0 0-53" stroke="#766e5b" strokeWidth="3" />
            <path
              d="m-5-22-7-41h22l4 38Z"
              fill="#f7ebcd"
              stroke="#a99b7d"
              strokeWidth="2"
            />
          </g>
        ))}
        <circle r="9" fill="#a06b48" stroke="#fff0c9" strokeWidth="3" />
      </g>
    </g>
  )
}

export function FarmDrawing({
  room,
  sleeping,
  growing,
  decor,
  tint,
  preview = false,
  pasture = false,
}: {
  room: Room
  sleeping: boolean
  growing: boolean
  decor: string
  tint?: string
  preview?: boolean
  pasture?: boolean
}) {
  const id = `farm-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const night = sleeping || decor === 'lavender'
  const sunset = decor === 'peach'
  return (
    <svg
      className={`${preview ? '' : sceneStyles.drawing} ${styles.farm}`}
      viewBox="0 0 960 600"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      data-farm-room={room}
      data-farm-night={night}
    >
      <defs>
        <linearGradient id={`${id}-sky`} x2="0" y2="1">
          <stop
            stopColor={night ? '#334961' : (tint ?? (sunset ? '#e7ac88' : '#b9d9d9'))}
          />
          <stop offset="1" stopColor={night ? '#82928d' : '#fff0c5'} />
        </linearGradient>
        <linearGradient id={`${id}-field`} x2="0" y2="1">
          <stop stopColor={night ? '#718565' : '#a9bc71'} />
          <stop offset="1" stopColor={night ? '#526b55' : '#7e9f63'} />
        </linearGradient>
      </defs>
      <rect width="960" height="600" fill={`url(#${id}-sky)`} />
      <circle cx="745" cy="100" r="38" fill={night ? '#fff3c8' : '#ffe5a0'} />
      {night && (
        <g fill="#fff4d4">
          {[110, 285, 465, 625, 835].map((x, i) => (
            <circle key={x} cx={x} cy={48 + (i % 3) * 31} r="2" />
          ))}
        </g>
      )}
      <g className={styles.clouds} fill="#fff9e8" opacity={night ? '.18' : '.65'}>
        <path d="M95 130q-10-26 22-28 7-36 43-19 30-11 41 18 31 4 20 29Z" />
        <path d="M455 91q-9-22 19-25 8-29 32-17 29-8 35 17 24 4 17 25Z" />
      </g>
      <path
        d="M0 277Q170 154 354 277T715 253T960 248V600H0Z"
        fill={night ? '#748d80' : '#bbca8e'}
      />
      <path d="M0 336Q214 246 429 319T960 285V600H0Z" fill={`url(#${id}-field)`} />
      <g stroke={night ? '#89946a' : '#c8cc82'} strokeWidth="8" opacity=".65">
        <path d="M490 302 696 385m-158-90 224 86m-174-85 248 80m-192-82 265 80m-221-84 268 63" />
      </g>
      <g transform="translate(88 164) scale(.94)">
        <FarmBarn />
      </g>
      <g transform="translate(817 150) scale(.84)">
        <FarmWindmill rotation={preview ? 15 : undefined} />
      </g>
      <g transform="translate(290 305)">
        <rect width="64" height="35" rx="9" fill="#d5af60" />
        <path
          d="M5 9h54M4 19h56M6 28h52M20 0v35m25-35v35"
          stroke="#a88a45"
          strokeWidth="2"
        />
      </g>
      <g transform="translate(677 330)">
        <ellipse cx="0" cy="7" rx="37" ry="21" fill="#faf2d9" />
        <path
          d="M-27 19v24m42-23v23"
          stroke="#f5e9ca"
          strokeWidth="10"
          strokeLinecap="round"
        />
        <path
          d="M-22-10q-18 2-13 17t24 4q13-21-11-21m33 0q-9 7-2 17t22-5"
          fill="#6a5a45"
        />
        <path
          className={preview ? undefined : styles.tail}
          d="M-35 4q-21-4-16 19"
          fill="none"
          stroke="#6a5a45"
          strokeWidth="4"
        />
        <path
          d="m27-11-8-9m29 8 8-8"
          stroke="#d9bf8f"
          strokeWidth="5"
          strokeLinecap="round"
        />
        <ellipse cx="38" cy="1" rx="16" ry="21" fill="#fff3d8" />
        <ellipse cx="38" cy="13" rx="18" ry="11" fill="#dbab8d" />
        <path
          d="M30-3v3m15-3v3m-13 12v2m11-2v2"
          stroke="#634d3e"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </g>
      <g stroke="#f0ddb0" strokeWidth="9" strokeLinecap="round" fill="none">
        <path d="M0 363H363m265 0h332M0 391h363m265 0h332" />
        {[25, 115, 205, 295, 665, 755, 845, 935].map((x) => (
          <path key={x} d={`M${x} 345v67`} />
        ))}
      </g>
      <path
        d="M380 354Q476 332 569 360L716 600H216Z"
        fill={night ? '#a59977' : '#deca97'}
      />
      <path
        d="M377 402q101-27 221 15M328 505q136-34 318 9"
        fill="none"
        stroke="#f7e1b1"
        strokeWidth="3"
        opacity=".45"
      />
      <ellipse
        cx="476"
        cy="521"
        rx="166"
        ry="29"
        fill={night ? '#526952' : '#a8b774'}
        opacity=".55"
      />
      {room === 'kitchen' && (
        <g data-farm-detail="feeding" transform="translate(710 444)">
          <path d="M0 18v62m137-62v62" stroke="#806046" strokeWidth="12" />
          <rect x="-15" width="170" height="23" rx="7" fill="#b27e52" />
          <path
            d="M20-25h91q-5 29-45 29T20-25"
            fill="#ded9bf"
            stroke="#9a9277"
            strokeWidth="3"
          />
          {pasture ? (
            <g
              data-sheep-hay
              fill="none"
              stroke="#c0a44d"
              strokeWidth="4"
              strokeLinecap="round"
            >
              <path d="m33-25 14-22m-5 22 16-27m-7 27 15-23m-4 23 14-26m-5 26 16-20m-9 20 20-15" />
              <path
                d="m38-26 8-12m4 14 10-16m11 15 9-12m5 12 10-9"
                stroke="#e4d173"
                strokeWidth="3"
              />
            </g>
          ) : (
            <g fill="#b3774e">
              <circle cx="43" cy="-25" r="9" />
              <circle cx="66" cy="-27" r="10" />
              <circle cx="88" cy="-24" r="9" />
            </g>
          )}
          <path d="M-8-55h36l-3 45H-5Z" fill="#f3ead6" />
          <path d="M-8-53h36" stroke="#b78562" strokeWidth="6" />
        </g>
      )}
      {room === 'bath' && (
        <g data-farm-detail="bath" transform="translate(719 462)">
          <ellipse cx="66" cy="3" rx="87" ry="29" fill="#e7dcbf" />
          <path
            d="M-20 1q4 78 86 79t88-79"
            fill="#b38a5c"
            stroke="#806345"
            strokeWidth="4"
          />
          <path
            d="M-13 31q80 38 160 0m-146 27q63 25 133 0"
            fill="none"
            stroke="#726c58"
            strokeWidth="7"
          />
          <ellipse cx="66" cy="0" rx="79" ry="23" fill="#9cc7c5" />
          <g className={styles.water} fill="none" stroke="#e9faf0" strokeWidth="3">
            <ellipse cx="60" cy="0" rx="45" ry="9" />
            <circle cx="108" cy="-16" r="12" />
            <circle cx="118" cy="-39" r="7" />
          </g>
        </g>
      )}
      {room === 'bedroom' && (
        <g data-farm-detail="rest" transform="translate(695 425)">
          <path d="M7 44 75-13l70 57v100H7Z" fill="#bc8c59" />
          <path
            d="m-7 49 82-75 84 75"
            fill="none"
            stroke="#7c5641"
            strokeWidth="13"
            strokeLinecap="round"
          />
          <path d="M49 144V82a28 28 0 0 1 56 0v62" fill="#6c503e" />
          <ellipse cx="77" cy="142" rx="54" ry="14" fill="#d8b765" />
          <path
            d="m37 141 12-6m6 14 15-10m20 10 8-12m9 8 9-8"
            stroke="#f0d68d"
            strokeWidth="3"
          />
        </g>
      )}
      {room === 'garden' && pasture && (
        <g data-farm-detail="pasture" transform="translate(760 450)">
          <ellipse cy="70" rx="112" ry="29" fill="#8f7649" />
          {[-74, -47, -20, 7, 34, 61, 88].map((x, i) => (
            <g key={x} transform={`translate(${x} ${(i % 2) * 12})`}>
              <path
                d={growing ? 'M0 70Q-8 25 0-15' : 'M0 70q-3-15 2-23'}
                fill="none"
                stroke={growing ? '#a08b3e' : '#678a4c'}
                strokeWidth="3"
              />
              {(growing ? [5, 20, 35] : [55]).map((y) => (
                <g key={y} fill={growing ? '#dec466' : '#9bb868'}>
                  <ellipse
                    cx="-6"
                    cy={y}
                    rx="5"
                    ry="11"
                    transform={`rotate(-35 -6 ${y})`}
                  />
                  <ellipse
                    cx="5"
                    cy={y + 4}
                    rx="5"
                    ry="11"
                    transform={`rotate(35 5 ${y + 4})`}
                  />
                </g>
              ))}
            </g>
          ))}
          <path
            d="M-108 50v55m55-48v53m68-49v53m79-61v53m-205-20 212-2"
            fill="none"
            stroke="#ba8d5d"
            strokeWidth="7"
            strokeLinecap="round"
          />
        </g>
      )}
      {room === 'garden' && !pasture && (
        <g data-farm-detail="orchard" transform="translate(757 427)">
          <path
            d="M0 89V-34m0 44-35-31m35 14 27-28"
            fill="none"
            stroke="#8f6947"
            strokeWidth="13"
          />
          <path
            d="M-68-15q-28-49 11-68-1-33 41-33 39-35 62 1 50 2 41 48 25 37-24 52-37 25-65 2-37 17-66-2"
            fill="#6d9359"
          />
          {growing && (
            <g fill="#db8160">
              {[
                [-41, -60],
                [26, -83],
                [51, -35],
                [-8, -24],
              ].map(([x, y]) => (
                <circle key={x} cx={x} cy={y} r="10" />
              ))}
            </g>
          )}
          <path d="M-57 89h114" stroke="#7f6242" strokeWidth="10" strokeLinecap="round" />
        </g>
      )}
      {room === 'home' && (
        <g data-farm-detail="hen" transform="translate(759 476)">
          <path d="m-13 7-20-18 9 28" fill="#f3e7c9" />
          <ellipse rx="24" ry="19" fill="#fff3d6" />
          <circle cx="20" cy="-17" r="13" fill="#fff3d6" />
          <path d="m13-29 3-9 6 8 6-6 1 11" fill="#c96348" />
          <path
            d="m32-18 13 5-14 5M-5 17v15m18-16v16"
            fill="#dda65b"
            stroke="#dda65b"
            strokeWidth="3"
          />
          <circle cx="23" cy="-20" r="2" fill="#594537" />
          <path d="M-12-2q10 19 21 1" fill="none" stroke="#dec89c" strokeWidth="3" />
          {[44, 70].map((x) => (
            <g key={x} transform={`translate(${x} 27)`}>
              <ellipse rx="10" ry="8" fill="#f2cf6d" />
              <circle cx="7" cy="-6" r="6" fill="#f8dc83" />
              <circle cx="9" cy="-7" r="1" fill="#62482f" />
            </g>
          ))}
        </g>
      )}
      <g
        className={preview ? undefined : styles.flowers}
        stroke="#5f824a"
        strokeWidth="4"
      >
        {[55, 99, 152, 885, 924].map((x, i) => (
          <g key={x} transform={`translate(${x} ${535 + (i % 2) * 28})`}>
            <path d="M0 43V0m0 29q-21-25-26-9m26 2q19-28 27-12" fill="none" />
            <g fill="#ecc468" stroke="#ecc468" strokeWidth="13">
              <path d="M-15 0h30M0-15v30m-10-10 20-20m-20 0 20 20" />
            </g>
            <circle r="10" fill="#836039" stroke="none" />
          </g>
        ))}
      </g>
      {night && <rect width="960" height="600" fill="#203348" opacity=".12" />}
    </svg>
  )
}
